import { useMemo, useState } from "react";
import { Modal } from "flowbite-react";
import { useAppStore } from "@/common/states/store";
import { useCompanyStore } from "@/common/states/store/Company";
import { useGetScoreOperation } from "./hooks";
import { format, startOfDay } from "date-fns";
import Loading from "@/common/components/ui/loading/Loading";
import { getCompanySigla } from "@/common/helpers/company.helpers";
import DatePicker from "react-datepicker";
import { HiMiniCalendarDays } from "react-icons/hi2";
import "react-datepicker/dist/react-datepicker.css";
import { exportScoreOperationToExcel } from "./export-excel/export";
import { HiDownload } from "react-icons/hi";
import { formatDate } from "@/common/helpers/date.helpers";

const AllCitiesScore = ({
  scores,
}: {
  scores: { city: string; score: number }[];
}) => {
  return (
    <div className="space-y-2">
      {scores
        ?.filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score)
        .map(({ city, score }) => (
          <div key={city} className="flex justify-between border-b py-2">
            <span>{getCompanySigla(city)}</span>
            <span
              className={`font-semibold ${
                score > 8.99 ? "text-green-600" : "text-gray-800"
              }`}
            >
              {Number(score).toFixed(2)}
            </span>
          </div>
        ))}
    </div>
  );
};

const CityLast15DaysScore = ({
  scores,
}: {
  scores: { date: string; score: number }[];
}) => {
  return (
    <div className="space-y-2">
      {scores?.map(({ date, score }) => (
        <div key={date} className="flex justify-between border-b py-2">
          <span>{date}</span>
          <span
            className={`font-semibold ${
              score > 8.99 ? "text-green-600" : "text-gray-800"
            }`}
          >
            {Number(score).toFixed(2)}
          </span>
        </div>
      ))}
    </div>
  );
};

export const ModalScoreOperation = () => {
  const {
    dashboard: {
      fleet: {
        modals: { scoreOperation },
        setCloseModalScoreOperation,
      },
    },
  } = useAppStore();

  const { filterMode, selectedCompanies, selectedUf } = useCompanyStore();
  const { selectedCity, isAllCities } = useMemo(() => {
    if (filterMode === "all") {
      return { selectedCity: "Brasil", isAllCities: true };
    }
    if (filterMode === "uf" && selectedUf) {
      return { selectedCity: selectedUf, isAllCities: true };
    }
    if (filterMode === "cities") {
      if (selectedCompanies.length === 1) {
        return {
          selectedCity:
            getCompanySigla(selectedCompanies[0].companyId) ||
            selectedCompanies[0].companyName,
          isAllCities: false,
        };
      } else {
        return { selectedCity: "Múltiplas Cidades", isAllCities: true };
      }
    }
    return { selectedCity: "Brasil", isAllCities: true };
  }, [filterMode, selectedCompanies, selectedUf]);

  // Estados do filtro de data (não aplicados ainda)
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // Estados para o filtro aplicado (enviado para API)
  const [appliedStartDate, setAppliedStartDate] = useState<
    string | undefined
  >();
  const [appliedEndDate, setAppliedEndDate] = useState<string | undefined>();

  const { data: operationsScore, loading } = useGetScoreOperation(
    appliedStartDate && appliedEndDate
      ? {
          startDate: appliedStartDate,
          endDate: appliedEndDate,
        }
      : undefined
  );

  const handleDateChange = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handleClearDates = () => {
    setStartDate(null);
    setEndDate(null);
    setAppliedStartDate(undefined);
    setAppliedEndDate(undefined);
  };

  const handleApplyFilter = () => {
    if (startDate && endDate) {
      // Garantir que a data está no início do dia sem ajuste de fuso horário
      const normalizedStartDate = startOfDay(startDate);
      const normalizedEndDate = startOfDay(endDate);

      setAppliedStartDate(format(normalizedStartDate, "yyyy-MM-dd"));
      setAppliedEndDate(format(normalizedEndDate, "yyyy-MM-dd"));
    }
  };

  const isFilterChanged = useMemo(() => {
    if (!startDate || !endDate) return false;

    const currentStart = format(startDate, "yyyy-MM-dd");
    const currentEnd = format(endDate, "yyyy-MM-dd");

    return currentStart !== appliedStartDate || currentEnd !== appliedEndDate;
  }, [startDate, endDate, appliedStartDate, appliedEndDate]);

  const { allCitiesScores, cityLast15Days } = useMemo(() => {
    const safeOperationsScore = Array.isArray(operationsScore)
      ? operationsScore
      : [];

    if (isAllCities) {
      const grouped = safeOperationsScore.reduce(
        (acc: Record<string, number[]>, op) => {
          acc[op.companyId] = acc[op.companyId] || [];
          acc[op.companyId].push(op.noteOperation);
          return acc;
        },
        {}
      );

      const allCitiesScores = Object.entries(grouped).map(([city, scores]) => ({
        city,
        score: scores.reduce((a, b) => a + b, 0) / scores.length,
      }));

      return { allCitiesScores, cityLast15Days: [] };
    } else {
      const groupedByDate = safeOperationsScore.reduce(
        (acc: Record<string, number[]>, op) => {
          const [yearStr, monthStr, dayStr] = op.date.split("-");
          const year = Number(yearStr);
          const month = Number(monthStr);
          const day = Number(dayStr);
          const dateKey = format(new Date(year, month - 1, day), "dd/MM/yyyy");
          acc[dateKey] = acc[dateKey] || [];
          acc[dateKey].push(op.noteOperation);
          return acc;
        },
        {}
      );

      const cityLast15Days = Object.entries(groupedByDate).map(
        ([date, scores]) => ({
          date,
          score: scores.reduce((a, b) => a + b, 0) / scores.length,
        })
      );

      return { allCitiesScores: [], cityLast15Days };
    }
  }, [operationsScore, isAllCities]);

  const modalTitle = useMemo(() => {
    // Se há filtro aplicado, mostrar o período filtrado
    if (appliedStartDate && appliedEndDate) {
      // Criar datas sem conversão de fuso horário
      const [yearStart, monthStart, dayStart] = appliedStartDate
        .split("-")
        .map(Number);
      const [yearEnd, monthEnd, dayEnd] = appliedEndDate.split("-").map(Number);

      const startFormatted = format(
        new Date(yearStart, monthStart - 1, dayStart),
        "dd/MM/yyyy"
      );
      const endFormatted = format(
        new Date(yearEnd, monthEnd - 1, dayEnd),
        "dd/MM/yyyy"
      );

      if (selectedCity === "*") {
        return (
          <>
            Notas de Operações - Brasil
            <span className="flex text-sm text-gray-600">
              ({startFormatted} - {endFormatted})
            </span>
          </>
        );
      }

      return (
        <>
          Notas de Operações - {selectedCity}
          <span className="flex text-sm text-gray-600">
            ({startFormatted} - {endFormatted})
          </span>
        </>
      );
    }

    // Comportamento padrão sem filtro
    if (selectedCity === "*") return "Notas de Operações - Brasil";

    if (cityLast15Days?.length) {
      const first = cityLast15Days[0].date;
      const last = cityLast15Days[cityLast15Days.length - 1].date;
      return (
        <>
          Notas de Operações - {selectedCity}{" "}
          <span className="flex text-sm text-gray-600">
            ({first} - {last})
          </span>
        </>
      );
    }

    return `Notas de Operações - ${selectedCity}`;
  }, [selectedCity, cityLast15Days, appliedStartDate, appliedEndDate]);

  const handleExportToExcel = () => {
    exportScoreOperationToExcel({
      data: isAllCities ? allCitiesScores : cityLast15Days,
      isAllCities,
      cityName: selectedCity,
      getSiglaFn: getCompanySigla,
    });
  };

  return (
    <Modal show={scoreOperation} onClose={setCloseModalScoreOperation}>
      <Modal.Header>
        <h3 className="text-black text-lg">{modalTitle}</h3>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filtrar por período:
          </label>
          <div className="flex items-center gap-2">
            <DatePicker
              locale="pt-Br"
              selectsRange
              maxDate={new Date()}
              startDate={startDate || undefined}
              endDate={endDate || undefined}
              onChange={handleDateChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholderText="Selecione o período"
              dateFormat="dd/MM/yyyy"
              onFocus={(e) => ((e.target as HTMLInputElement).readOnly = true)}
              icon={<HiMiniCalendarDays />}
              showIcon={!endDate}
              toggleCalendarOnIconClick={true}
              swapRange
            />

            <button
              onClick={handleApplyFilter}
              disabled={!startDate || !endDate || !isFilterChanged}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              title="Aplicar filtro"
            >
              Aplicar
            </button>

            {(startDate || endDate || appliedStartDate) && (
              <button
                onClick={handleClearDates}
                className="px-3 py-2 text-sm font-medium text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                title="Limpar filtro"
              >
                ✕
              </button>
            )}
          </div>

          {appliedStartDate && appliedEndDate && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                ✓ Filtro aplicado
              </span>
              <p className="text-xs text-gray-600">
                {formatDate(new Date(appliedStartDate))} até{" "}
                {formatDate(new Date(appliedEndDate))}
              </p>
            </div>
          )}

          {/* Indicador de alteração pendente */}
          {isFilterChanged && (
            <p className="mt-2 text-xs text-orange-600">
              ⚠ Período alterado. Clique em "Aplicar" para atualizar os dados.
            </p>
          )}
        </div>

        {/* Conteúdo */}
        {loading && <Loading />}
        {!loading && (
          <>
            {!Array.isArray(operationsScore) || operationsScore.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>
                  Nenhum dado encontrado
                  {appliedStartDate ? " para o período selecionado" : ""}
                </p>
              </div>
            ) : isAllCities ? (
              <AllCitiesScore scores={allCitiesScores || []} />
            ) : (
              <CityLast15DaysScore scores={cityLast15Days || []} />
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="justify-end">
        <button
          onClick={handleExportToExcel}
          disabled={!operationsScore || operationsScore.length === 0 || loading}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          title="Exportar para Excel"
        >
          <HiDownload className="w-4 h-4" />
          <span>Exportar</span>
        </button>
      </Modal.Footer>
    </Modal>
  );
};
